import { Box, BoxProps, styled, Theme } from "@material-ui/core";
import React from "react";
import { filterProductItemsWithDefaultVariant } from "../../../helpers/product";
import { useExpressDeliveryAvailabilitySelector } from "../../../hooks/use-express-delivery-availability";
import { ProductListItemType } from "../../../types/airgraft";
import ProductCard from "../ProductCard";
import fastCompare from "react-fast-compare";

const singleColumnStyles = {
  maxWidth: `100% !important`,
  flex: "0 1 100% !important",
  marginRight: "0 !important"
};

export const List = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  "& > *": {
    // XXSmall: 1 column
    "@media (min-width: 0px) and (max-width: 275px)": singleColumnStyles,
    // Xsmall: 2 columns
    [theme.breakpoints.between("xs", "sm")]: {
      maxWidth: `calc(50% - ${theme.spacing(0.75)}px)`,
      flex: "0 1 50%",
      marginRight: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      "&:nth-child(2n+0)": {
        marginRight: 0
      }
    },
    // Md: 3 columns
    [theme.breakpoints.only("md")]: {
      maxWidth: `calc(33.33% - ${theme.spacing(1.33)}px)`,
      flex: "0 1 33.33%",
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2),
      "&:nth-child(3n+0)": {
        marginRight: 0
      }
    },
    // Lg+: 4 columns
    [theme.breakpoints.up("lg")]: {
      maxWidth: `calc(25% - ${theme.spacing(3)}px)`,
      flex: "0 1 25%",
      marginRight: theme.spacing(4),
      marginBottom: theme.spacing(4),
      "&:nth-child(4n+0)": {
        marginRight: 0
      }
    }
  }
}));

type Props = {
  products: ProductListItemType[];
  skeleton?: boolean;
  lazyload?: boolean;
} & BoxProps;

function ProductsList({ products, skeleton, ...boxProps }: Props) {
  const getProductExpressAvailability = useExpressDeliveryAvailabilitySelector(
    c => c.getProductExpressAvailability
  );
  const getAvailableProviderProductData = useExpressDeliveryAvailabilitySelector(
    c => c.getAvailableProviderProductData
  );

  return (
    <Box marginBottom={{ xs: 4, md: 12 }} {...boxProps}>
      <List>
        {filterProductItemsWithDefaultVariant(products).map(product => {
          return (
            <ProductCard
              key={product.slug}
              variant={skeleton ? "SKELETON" : "SHOP"}
              product={product}
              shopAvailabilityLabel={getProductExpressAvailability(product, {
                fallbackToDefault: true
              })} // Use express delivery availability (Based on current address)
              price={getAvailableProviderProductData(product)?.price?.amount}
            />
          );
        })}
      </List>
    </Box>
  );
}

export default React.memo(ProductsList, fastCompare);
